/**
 * Global Search Bar Component
 * Story 11.5: Search bar with keyboard shortcut and dropdown results
 */

'use client';

import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useGlobalSearch, useDebounce, useSearchHistory, getSearchResultLink } from '@/hooks/useGlobalSearch';
import { SearchResultsDropdown } from './SearchResultsDropdown';

export function GlobalSearchBar() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const debouncedQuery = useDebounce(query, 300); // Debounce 300ms
  const { history, addToHistory } = useSearchHistory();

  // Perform search with debounced query
  const { data, isLoading } = useGlobalSearch(
    debouncedQuery,
    undefined,
    undefined,
    isOpen && debouncedQuery.length >= 2
  );

  // Flatten all results for keyboard navigation
  const allResults = data?.results.flatMap((group) => group.results) || [];
  const maxIndex = allResults.length;

  // Keyboard shortcuts: Cmd+K / Ctrl+K, Arrow navigation, Enter
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        // Focus search input
        const input = document.getElementById('global-search-input');
        if (input) input.focus();
      }

      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setQuery('');
        setSelectedIndex(0);
      }

      // Arrow down: select next result
      if (e.key === 'ArrowDown' && isOpen && maxIndex > 0) {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % maxIndex);
      }

      // Arrow up: select previous result
      if (e.key === 'ArrowUp' && isOpen && maxIndex > 0) {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + maxIndex) % maxIndex);
      }

      // Enter: navigate to selected result
      if (e.key === 'Enter' && isOpen && maxIndex > 0) {
        e.preventDefault();
        const selectedResult = allResults[selectedIndex];
        if (selectedResult) {
          handleResultNavigation(selectedResult);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, maxIndex, allResults]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.length > 0);
    setSelectedIndex(0); // Reset selection when query changes
  };

  const handleResultNavigation = (result: any) => {
    if (debouncedQuery.length >= 2) {
      addToHistory(debouncedQuery);
    }
    const link = getSearchResultLink(result);
    if (link) {
      window.location.href = link;
    }
    setIsOpen(false);
    setQuery('');
    setSelectedIndex(0);
  };

  const handleResultClick = () => {
    if (debouncedQuery.length >= 2) {
      addToHistory(debouncedQuery);
    }
    setIsOpen(false);
    setQuery('');
    setSelectedIndex(0);
  };

  const handleViewAll = () => {
    if (debouncedQuery.length >= 2) {
      addToHistory(debouncedQuery);
    }
    // Navigate to full search results page
    window.location.href = `/search?q=${encodeURIComponent(debouncedQuery)}`;
  };

  return (
    <div className="relative w-full max-w-md">
      {/* Search input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          id="global-search-input"
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => query.length > 0 && setIsOpen(true)}
          placeholder="Search... (⌘K)"
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-brand-dark text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Dropdown results */}
      {isOpen && query.length >= 2 && (
        <>
          {/* Backdrop to close dropdown */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false);
              setQuery('');
            }}
          />

          {/* Dropdown content */}
          <div className="absolute top-full left-0 right-0 mt-2 z-20">
            <SearchResultsDropdown
              query={debouncedQuery}
              data={data}
              isLoading={isLoading}
              onResultClick={handleResultClick}
              onViewAll={handleViewAll}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default GlobalSearchBar;
